'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FAQItem {
  id: string;
  question: string;
  answer: string;
  category: string;
}

const FAQ = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [openFAQ, setOpenFAQ] = useState<string | null>(null);
  const [activeCategory, setActiveCategory] = useState('clinical');

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const categories = [
  { id: 'clinical', label: 'Clinical Questions', icon: 'BeakerIcon' },
  { id: 'implementation', label: 'Implementation', icon: 'WrenchScrewdriverIcon' },
  { id: 'regulatory', label: 'Regulatory & Compliance', icon: 'ShieldCheckIcon' },
  { id: 'support', label: 'Support & Training', icon: 'AcademicCapIcon' }];


  const faqs: FAQItem[] = [
  {
    id: '1',
    question: 'What neonatal brain complications does REVIVE treat?',
    answer: 'REVIVE is specifically designed to treat Hypoxic-Ischemic Encephalopathy (HIE) through therapeutic hypothermia with integrated brain monitoring. The system provides real-time monitoring and proactive intervention for all HIE severity levels, from mild to severe cases.',
    category: 'clinical'
  },
  {
    id: '2',
    question: 'What is Therapeutic Hypothermia?',
    answer: 'Therapeutic Hypothermia is a proven treatment in which a newborn\'s body temperature is carefully lowered to around 33–34°C for 72 hours to protect the brain after oxygen deprivation at birth.',
    category: 'clinical'
  },
  {
    id: '2a',
    question: 'Why is Therapeutic Hypothermia important for newborns?',
    answer: 'Lack of oxygen during birth can cause ongoing brain injury even after oxygen is restored. Cooling slows harmful brain processes, reduces inflammation, and improves survival and neurodevelopmental outcomes.',
    category: 'clinical'
  },
  {
    id: '2b',
    question: 'Is Therapeutic Hypothermia safe?',
    answer: 'Yes. When delivered using controlled medical devices and protocols, TH is safe and evidence-based, and is the global standard of care for moderate to severe HIE.',
    category: 'clinical'
  },
  {
    id: '5',
    question: 'What training is required for NICU staff?',
    answer: 'All clinical staff complete a 4-hour certification program covering device operation, clinical protocols, alarm management, and troubleshooting. We provide both on-site and virtual training options, with ongoing educational resources and refresher courses. Most NICUs achieve full staff certification within 2 weeks.',
    category: 'implementation'
  },
  {
    id: '7',
    question: 'What regulatory clearance does REVIVE have?',
    answer: 'REVIVE is CDSCO approved (License: MFGMD/2023/000619) as a Class II A medical device for neonatal brain monitoring and therapeutic hypothermia. We comply with ISO 13485:2016 and EN ISO 13485:2016 quality management standards. All regulatory documentation is available for review.',
    category: 'regulatory'
  },
  {
    id: '8',
    question: 'Is REVIVE compliant with Indian data privacy regulations?',
    answer: 'Yes, REVIVE is fully compliant with Indian data privacy regulations including the IT Act 2000 and upcoming Digital Personal Data Protection Act. We maintain end-to-end encryption for all patient data transmission and storage. All data handling meets or exceeds regulatory privacy requirements for healthcare institutions in India.',
    category: 'regulatory'
  },
  {
    id: '9',
    question: 'What ongoing support is provided after implementation?',
    answer: 'We provide 24/7 clinical specialist support via phone, email, and secure messaging. This includes technical troubleshooting, clinical consultation, and immediate response to urgent issues. Annual on-site service visits, software updates, and continuing education programs are included with all procurement options.',
    category: 'support'
  },
  {
    id: '10',
    question: 'How often does the device require maintenance?',
    answer: 'REVIVE requires annual calibration and preventive maintenance, which we schedule at your convenience to minimize disruption. Single-use sensors are replaced per patient, and all reusable components are designed for easy cleaning and sterilization.',
    category: 'support'
  }];


  const filteredFAQs = faqs.filter((faq) => faq.category === activeCategory);

  const toggleFAQ = (id: string) => {
    setOpenFAQ(openFAQ === id ? null : id);
  };

  return (
    <section id="faq" className="py-24 bg-surface">
      <div className="max-w-[1200px] mx-auto px-6">
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-4 py-2 rounded-2xl mb-6">
            <Icon name="QuestionMarkCircleIcon" size={20} />
            <span className="text-sm font-semibold">Frequently Asked Questions</span>
          </div>

          <h2 className="lg:text-5xl font-bold font-heading text-foreground mb-6 text-[32px]">
            Your Questions Answered
          </h2>

          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Find answers to common questions about REVIVE technology, implementation, and clinical use.
          </p>
        </div>

        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {categories.map((category) =>
          <button
            key={category.id}
            onClick={isHydrated ? () => setActiveCategory(category.id) : undefined}
            className={`flex items-center space-x-2 px-6 py-3 rounded-2xl font-semibold transition-all duration-250 ease-out ${
            activeCategory === category.id ?
            'bg-primary text-primary-foreground shadow-card' :
            'bg-card text-muted-foreground hover:bg-surface'}`
            }>

              <Icon name={category.icon as any} size={20} />
              <span>{category.label}</span>
            </button>
          )}
        </div>

        <div className="space-y-4 max-w-4xl mx-auto">
          {filteredFAQs.map((faq) =>
          <div key={faq.id} className="bg-card rounded-2xl shadow-card overflow-hidden">
              <button
              onClick={isHydrated ? () => toggleFAQ(faq.id) : undefined}
              className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-surface transition-colors duration-250 ease-out">

                <span className="font-semibold text-foreground pr-4">{faq.question}</span>
                <Icon
                name={openFAQ === faq.id ? 'ChevronUpIcon' : 'ChevronDownIcon'}
                size={24}
                className="text-muted-foreground flex-shrink-0" />

              </button>

              {openFAQ === faq.id &&
            <div className="px-6 pb-4">
                  <p className="text-muted-foreground leading-relaxed">{faq.answer}</p>
                </div>
            }
            </div>
          )}
        </div>

        <div className="mt-16 bg-gradient-to-br from-primary to-secondary rounded-2xl p-8 text-center text-primary-foreground">
          <Icon name="ChatBubbleLeftRightIcon" size={48} className="mx-auto mb-4" />
          <h3 className="text-2xl font-bold font-heading mb-4">
            Still Have Questions?
          </h3>
          <p className="text-lg opacity-90 mb-6 max-w-2xl mx-auto">
            Our medical device specialists are available to answer your specific questions and provide detailed information about REVIVE implementation at your institution.
          </p>
          <button className="bg-accent text-accent-foreground px-8 py-4 rounded-2xl font-cta font-bold text-lg shadow-cta hover:bg-accent/90 transition-all duration-250 ease-out">
            Schedule Consultation
          </button>
        </div>
      </div>
    </section>);

};

export default FAQ;